import Link from '@gik/core/components/Link.tsx';
import type WordpressSituation from '@gik/core/models/wordpress/WordpressSituation';
import routes from '@gik/core/routes';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import type { GiveTaxonomyType } from '@gik/shop/types/GiveTaxonomyType';
import { getSituationVariant } from '@gik/shop/utils/getSituationVariant';
import { Button } from '@gik/ui-legacy/Button';
import { Grid } from '@gik/ui-legacy/Grid';
import React from 'react';
import { GiveSituationTile } from './GiveSituationTile';

export type GiveSituationsGridProps = {
  // can pass initial or mock data
  items?: WordpressSituation[];
  situations?: WordpressSituation[];
  cols?: number;
  perPage?: number;
  loadMoreButton?: boolean;
  onLoadMore?(): void;
  skeleton?: boolean;
  skeletonItems?: number;
  skeletonCategoryItems?: number;
  taxonomyType?: GiveTaxonomyType;
  parentTaxonomy?: string;
} & UIComponent;

function GiveSituationsGridComp({
  perPage = 12,
  cols = 5,
  loadMoreButton,
  items,
  situations,
  className,
}: GiveSituationsGridProps): React.ReactElement {
  const bem = useBemCN('give-situations-grid');

  const [page, setPage] = React.useState<number>(1);
  const totalPages = 10;

  function handleLoadMore() {
    // TODO: this should perform backend pagination when our rest api and cache server supports it
    // for now just show all situations in one go and hide the load more button
    setPage(totalPages);
  }

  function getLinkUrl(item: WordpressSituation) {
    const basePath = `/situation`;
    if (item.parent === 0) return `${basePath}/${item.slug}`;
    return `${basePath}/${getParentSituation(item)?.slug}/${item.slug}`;
  }

  function getParentSituation(item: WordpressSituation) {
    return situations?.find(situation => situation.id === item.parent);
  }

  if (items?.length === 0) {
    return <div {...bem('empty')}>Empty</div>;
  }

  return (
    <div {...bem(null, null, className)}>
      <Grid cols={cols} {...bem('grid')}>
        {items
          ?.filter(item => item.parent !== 0)
          .map((item, index) => {
            if (index >= page * perPage) return null;
            return (
              <Link key={index} href={getLinkUrl(item)} scroll shallow {...bem('item')}>
                <GiveSituationTile
                  variant={getSituationVariant(item, situations)}
                  title={item.name}
                  imageUrl={item.acf.taxonomy_svg_icon}
                />
              </Link>
            );
          })}
      </Grid>

      {loadMoreButton && items?.length > perPage && page === 1 && (
        <Button {...bem('btn-more')} onClick={handleLoadMore} variant="default" pill>
          Show All
        </Button>
      )}
    </div>
  );
}

export const GiveSituationsGrid = withComponentErrorBoundary(GiveSituationsGridComp);

export const GiveSituationsGridSkeleton = ({
  skeletonItems = 10,
  cols = 5,
  loadMoreButton,
}: Partial<GiveSituationsGridProps>): React.ReactElement => {
  const bem = useBemCN(`give-situations-grid`);
  return (
    <div {...bem()}>
      <Grid cols={cols} {...bem('grid')}>
        {Array.from(Array(skeletonItems).keys()).map(index => {
          return <GiveSituationTile key={index} />;
        })}
      </Grid>
      {loadMoreButton && (
        <Button variant="default" pill disabled {...bem('btn-more')}>
          Show All
        </Button>
      )}
    </div>
  );
};
